/**
 * Computer class for interacting with Orgo virtual environments
 */

import { ApiClient } from './api/client';
import { Project } from './project';
import { getProvider } from './prompt';
import { v4 as uuidv4 } from 'uuid';
import type {
  ComputerInfo,
  ScreenshotOptions,
  ScrollOptions,
  PromptOptions,
  PromptMessage,
  ExecResponse,
  StreamStartResponse,
  StreamStatusResponse,
  StreamStopResponse
} from './types';

export class Computer {
  private apiKey: string;
  private baseApiUrl?: string;
  private api: ApiClient;
  public projectName?: string;
  public projectId?: string;
  public computerId!: string;
  public name?: string;
  
  // Configuration
  private os: 'linux' | 'windows';
  private ram: 1 | 2 | 4 | 8 | 16 | 32 | 64;
  private cpu: 1 | 2 | 4 | 8 | 16;
  private gpu: 'none' | 'a10' | 'l40s' | 'a100-40gb' | 'a100-80gb';

  private constructor(
    apiKey: string,
    baseApiUrl?: string,
    os: 'linux' | 'windows' = 'linux',
    ram: 1 | 2 | 4 | 8 | 16 | 32 | 64 = 2,
    cpu: 1 | 2 | 4 | 8 | 16 = 2,
    gpu: 'none' | 'a10' | 'l40s' | 'a100-40gb' | 'a100-80gb' = 'none'
  ) {
    this.apiKey = apiKey;
    this.baseApiUrl = baseApiUrl;
    this.api = new ApiClient(this.apiKey, this.baseApiUrl);
    this.os = os;
    this.ram = ram;
    this.cpu = cpu;
    this.gpu = gpu;
  }

  /**
   * Create a new Computer instance
   * 
   * @param options Configuration options
   * @param options.project Project name (string) or Project instance. If not provided, creates a new project.
   * @param options.name Computer name within the project (optional, auto-generated if not provided)
   * @param options.computerId Existing computer ID to connect to (optional)
   * @param options.apiKey Orgo API key (defaults to ORGO_API_KEY env var)
   * @param options.baseApiUrl Custom API URL (optional)
   * @param options.ram RAM in GB (1, 2, 4, 8, 16, 32, or 64) - only used when creating
   * @param options.memory Alternative parameter for RAM in GB - only used when creating
   * @param options.cpu CPU cores (1, 2, 4, 8, or 16) - only used when creating
   * @param options.os Operating system ("linux" or "windows") - only used when creating
   * @param options.gpu GPU type - only used when creating
   */
  public static async create(options?: {
    project?: string | Project;
    name?: string;
    computerId?: string;
    apiKey?: string;
    baseApiUrl?: string;
    ram?: 1 | 2 | 4 | 8 | 16 | 32 | 64;
    memory?: 1 | 2 | 4 | 8 | 16 | 32 | 64;
    cpu?: 1 | 2 | 4 | 8 | 16;
    os?: 'linux' | 'windows';
    gpu?: 'none' | 'a10' | 'l40s' | 'a100-40gb' | 'a100-80gb';
  }): Promise<Computer> {
    // Safe environment variable access
    let envApiKey = '';
    if (typeof process !== 'undefined' && process.env && process.env.ORGO_API_KEY) {
      envApiKey = process.env.ORGO_API_KEY;
    }
    
    const apiKey = options?.apiKey || envApiKey || '';
    
    // Handle memory parameter as an alias for ram
    let ram = options?.ram;
    if (ram === undefined && options?.memory !== undefined) {
      ram = options.memory;
    }

    const computer = new Computer(
      apiKey,
      options?.baseApiUrl,
      options?.os || 'linux',
      ram || 2,
      options?.cpu || 2,
      options?.gpu || 'none'
    );

    if (options?.computerId) {
      // Just store the computer ID, no API call needed
      computer.computerId = options.computerId;
      computer.name = options.name;
      console.info(`Connected to computer ID: ${computer.computerId}`);
    } else if (options?.project) {
      // Work with specified project
      if (typeof options.project === 'string') {
        // Project name provided
        await computer._initializeWithProjectName(options.project, options?.name);
      } else if (options.project instanceof Project) {
        // Project instance provided
        await computer._initializeWithProjectInstance(options.project, options?.name);
      } else {
        throw new Error('project must be a string (project name) or Project instance');
      }
    } else {
      // No project specified, create a new one
      await computer._createNewProjectAndComputer(options?.name);
    }

    return computer;
  }

  private async _initializeWithProjectName(projectName: string, computerName?: string): Promise<void> {
    this.projectName = projectName;
    
    try {
      // Try to get existing project
      const project = await this.api.getProjectByName(projectName);
      this.projectId = project.id;
      
      // Check for existing computers
      const computers = await this.api.listComputers(this.projectId);
      
      if (computerName) {
        // Look for specific computer
        const existing = computers.find(c => c.name === computerName);
        if (existing) {
          await this._connectToExistingComputer(existing);
        } else {
          // Create new computer with specified name
          await this._createComputer(this.projectId, computerName);
        }
      } else if (computers.length > 0) {
        // No name specified, use first available computer
        const firstComputer = computers[0];
        if (firstComputer) {
          await this._connectToExistingComputer(firstComputer);
        } else {
          // Should not happen, but handle gracefully
          await this._createComputer(this.projectId, computerName);
        }
      } else {
        // No computers exist, create new one
        await this._createComputer(this.projectId, computerName);
      }
    } catch (error) {
      // Project doesn't exist, create it
      console.info(`Project ${projectName} not found, creating new project`);
      const project = await this.api.createProject(projectName);
      this.projectId = project.id;
      await this._createComputer(this.projectId, computerName);
    }
  }

  private async _initializeWithProjectInstance(project: Project, computerName?: string): Promise<void> {
    this.projectName = project.name;
    this.projectId = project.id;
    
    const computers = await project.listComputers();
    
    if (computerName) {
      // Look for specific computer
      const existing = computers.find(c => c.name === computerName);
      if (existing) {
        await this._connectToExistingComputer(existing);
      } else {
        // Create new computer with specified name
        await this._createComputer(this.projectId, computerName);
      }
    } else if (computers.length > 0) {
      // No name specified, use first available computer
      const firstComputer = computers[0];
      if (firstComputer) {
        await this._connectToExistingComputer(firstComputer);
      } else {
        // Should not happen, but handle gracefully
        await this._createComputer(this.projectId, computerName);
      }
    } else {
      // No computers exist, create new one
      await this._createComputer(this.projectId, computerName);
    }
  }

  private async _createNewProjectAndComputer(computerName?: string): Promise<void> {
    // Generate a unique project name
    const projectName = `project-${uuidv4().substring(0, 8)}`;
    
    // Create the project
    const project = await this.api.createProject(projectName);
    this.projectId = project.id;
    this.projectName = projectName;
    
    // Create a computer in the new project
    await this._createComputer(this.projectId, computerName);
  }

  private async _connectToExistingComputer(computerInfo: ComputerInfo): Promise<void> {
    this.computerId = computerInfo.id;
    this.name = computerInfo.name;
    console.info(`Connected to existing computer ${this.name} (ID: ${this.computerId})`);
  }

  private async _createComputer(projectId: string, computerName?: string): Promise<void> {
    // Generate name if not provided
    if (!computerName) {
      computerName = `desktop-${uuidv4().substring(0, 8)}`;
    }
    
    this.name = computerName;
    
    // Validate parameters
    const validRam = [1, 2, 4, 8, 16, 32, 64];
    const validCpu = [1, 2, 4, 8, 16];
    const validOs = ['linux', 'windows'];
    const validGpu = ['none', 'a10', 'l40s', 'a100-40gb', 'a100-80gb'];
    
    if (!validRam.includes(this.ram)) {
      throw new Error(`ram must be one of: ${validRam.join(', ')} GB`);
    }
    if (!validCpu.includes(this.cpu)) {
      throw new Error(`cpu must be one of: ${validCpu.join(', ')} cores`);
    }
    if (!validOs.includes(this.os)) {
      throw new Error(`os must be either 'linux' or 'windows'`);
    }
    if (!validGpu.includes(this.gpu)) {
      throw new Error(`gpu must be one of: ${validGpu.join(', ')}`);
    }
    
    const computer = await this.api.createComputer(
      projectId,
      computerName,
      this.os,
      this.ram,
      this.cpu,
      this.gpu
    );
    
    this.computerId = computer.id;
    console.info(`Created new computer ${this.name} (ID: ${this.computerId})`);
  }

  public async status(): Promise<ComputerInfo> {
    return this.api.getComputer(this.computerId);
  }

  public async restart(): Promise<unknown> {
    return this.api.restartComputer(this.computerId);
  }

  public async destroy(): Promise<unknown> {
    return this.api.deleteComputer(this.computerId);
  }

  // Navigation methods
  public async leftClick(x: number, y: number): Promise<unknown> {
    return this.api.leftClick(this.computerId, x, y);
  }

  public async rightClick(x: number, y: number): Promise<unknown> {
    return this.api.rightClick(this.computerId, x, y);
  }

  public async doubleClick(x: number, y: number): Promise<unknown> {
    return this.api.doubleClick(this.computerId, x, y);
  }

  public async drag(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    button: 'left' | 'right' = 'left',
    duration: number = 0.5
  ): Promise<unknown> {
    return this.api.drag(this.computerId, startX, startY, endX, endY, button, duration);
  }

  public async scroll(direction: ScrollOptions['direction'] = 'down', amount: number = 3): Promise<unknown> {
    return this.api.scroll(this.computerId, direction, amount);
  }

  // Input methods
  public async type(text: string): Promise<unknown> {
    return this.api.typeText(this.computerId, text);
  }

  public async key(key: string): Promise<unknown> {
    return this.api.keyPress(this.computerId, key);
  }

  // View methods
  public async screenshot(options?: ScreenshotOptions): Promise<Buffer | string> {
    const response = await this.api.getScreenshot(this.computerId);
    const imageData = response.image || '';

    if (imageData.startsWith('http://') || imageData.startsWith('https://')) {
      const fetch = (await import('cross-fetch')).default;
      const imgResponse = await fetch(imageData);
      if (!imgResponse.ok) {
        throw new Error(`Failed to download screenshot: ${imgResponse.statusText}`);
      }
      const arrayBuffer = await imgResponse.arrayBuffer();
      const buffer = Buffer.from(arrayBuffer);
      
      if (options?.format === 'base64') {
        return buffer.toString('base64');
      }
      return buffer;
    } else {
      if (options?.format === 'base64') {
        return imageData;
      }
      return Buffer.from(imageData, 'base64');
    }
  }

  public async screenshotBase64(): Promise<string> {
    return this.screenshot({ format: 'base64' }) as Promise<string>;
  }

  // Execution methods
  public async bash(command: string): Promise<string> {
    const response = await this.api.executeBash(this.computerId, command);
    return response.output || '';
  }

  public async exec(code: string, timeout: number = 10): Promise<ExecResponse> {
    return this.api.executePython(this.computerId, code, timeout);
  }

  public async wait(seconds: number): Promise<unknown> {
    return this.api.wait(this.computerId, seconds);
  }

  // Streaming methods
  public async startStream(connection: string): Promise<StreamStartResponse> {
    return this.api.startStream(this.computerId, connection);
  }

  public async stopStream(): Promise<StreamStopResponse> {
    return this.api.stopStream(this.computerId);
  }

  public async streamStatus(): Promise<StreamStatusResponse> {
    return this.api.getStreamStatus(this.computerId);
  }

  // AI control method
  public async prompt(options: PromptOptions): Promise<PromptMessage[]> {
    const providerInstance = getProvider(options.provider || 'anthropic');

    return providerInstance.execute({
      computerId: this.computerId,
      instruction: options.instruction,
      callback: options.callback,
      apiKey: options.apiKey,
      model: options.model || 'claude-3-7-sonnet-20250219',
      displayWidth: options.displayWidth || 1024,
      displayHeight: options.displayHeight || 768,
      thinkingEnabled: options.thinkingEnabled || false,
      thinkingBudget: options.thinkingBudget || 1024,
      maxTokens: options.maxTokens || 4096,
      maxIterations: options.maxIterations || 20,
      maxSavedScreenshots: options.maxSavedScreenshots || 5,
      orgoApiKey: this.apiKey,
      orgoBaseUrl: this.baseApiUrl
    });
  }
}